import React, { useState } from 'react';
import { degreePlanAPI } from '../services/api';
import { DegreePlan, UserProfile, DegreePlanFormData } from '../types';
import { storage } from '../utils/storage';

const DegreePlanPage: React.FC = () => {
  const [formData, setFormData] = useState<DegreePlanFormData>({
    interests: [],
    career_goal: '',
    weekly_hours: 12,
    learning_style: 'balanced',
    current_courses: [],
  });

  // Make userProfile editable
  const [userProfile, setUserProfile] = useState<UserProfile>(
    storage.getUserProfile() || { program: 'MSIT' }
  );

  // Number of semesters state
  const [numberOfSemesters, setNumberOfSemesters] = useState<number>(4);

  const [degreePlan, setDegreePlan] = useState<DegreePlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Credit requirements for each program
  const creditRequirements: { [key: string]: number } = {
    'MSEAI': 144,
    'MSIT': 144,
    'MSECE': 97
  };

  const interestOptions = [
    'Artificial Intelligence',
    'Machine Learning',
    'Cybersecurity',
    'Software Engineering',
    'Data Science',
    'Cloud Computing',
    'Mobile Development',
    'IoT',
    'Computer Vision',
    'Natural Language Processing',
  ];

  const handleInterestToggle = (interest: string) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));
  };

  const handleProgramChange = (program: 'MSIT' | 'MSEAI' | 'MSECE') => {
    setUserProfile((prev) => ({ ...prev, program }));
    storage.setUserProfile({ ...userProfile, program }); // persist
  };

  const handleGeneratePlan = async () => {
    try {
      setLoading(true);
      setError(null);

      const fullProfile: UserProfile = {
        ...userProfile,
        ...formData,
        numberOfSemesters: numberOfSemesters,
        requiredCredits: userProfile.program ? creditRequirements[userProfile.program] : 144
      };

      const response = await degreePlanAPI.generatePlan(fullProfile);
      setDegreePlan(response.degree_plan);
    } catch (err) {
      console.error('Error generating plan:', err);
      setError('Failed to generate degree plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderSemesterCourses = (semesterKey: string, courses: any[], semesterNumber: number) => {
    const semesterNames: { [key: string]: string } = {
      year_1_semester_1: 'Semester 1',
      year_1_semester_2: 'Semester 2',
      year_2_semester_1: 'Semester 3',
      year_2_semester_2: 'Semester 4',
    };

    // Calculate total credits for this semester
    const totalCredits = courses.reduce((sum, course) => sum + (course.units || 0), 0);

    return (
      <div key={semesterKey} className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-bold text-gray-900">
            {semesterNames[semesterKey] || `Semester ${semesterNumber}`}
          </h3>
          <span className="text-sm font-semibold text-[#C41230]">
            {totalCredits} credits
          </span>
        </div>
        {courses.length > 0 ? (
          <div className="space-y-2">
            {courses.map((course, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:shadow-md transition"
              >
                <div className="flex justify-between items-start mb-1">
                  <h4 className="font-semibold text-gray-900">{course.title}</h4>
                  <span className="bg-[#C41230] text-white px-2 py-1 rounded text-sm">
                    {course.units} units
                  </span>
                </div>
                {course.reason && (
                  <p className="text-sm text-gray-600 mt-1">{course.reason}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No courses scheduled</p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Degree Planning</h1>
          <p className="text-gray-600">
            Create a personalized academic plan based on your interests and goals
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel: Personalization Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Profile</h2>

              {/* Program Dropdown */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Program
                </label>
                <select
                  value={userProfile.program}
                  onChange={(e) =>
                    handleProgramChange(e.target.value as 'MSIT' | 'MSEAI' | 'MSECE')
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent"
                >
                  <option value="MSIT">MSIT</option>
                  <option value="MSEAI">MSEAI</option>
                  <option value="MSECE">MSECE</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Required credits: {userProfile.program ? creditRequirements[userProfile.program] : 144}
                </p>
              </div>

              {/* Number of Semesters Dropdown */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Number of Semesters
                </label>
                <select
                  value={numberOfSemesters}
                  onChange={(e) => setNumberOfSemesters(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent"
                >
                  <option value="1">1 Semester</option>
                  <option value="2">2 Semesters</option>
                  <option value="3">3 Semesters</option>
                  <option value="4">4 Semesters</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Credits per semester: ~{Math.ceil((userProfile.program ? creditRequirements[userProfile.program] : 144) / numberOfSemesters)}
                </p>
              </div>

              {/* Interests */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Interests (select multiple)
                </label>
                <div className="flex flex-wrap gap-2">
                  {interestOptions.map((interest) => (
                    <button
                      key={interest}
                      onClick={() => handleInterestToggle(interest)}
                      className={`px-3 py-1 rounded-full text-sm transition ${
                        formData.interests.includes(interest)
                          ? 'bg-[#C41230] text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              </div>

              {/* Career Goal */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Career Goal
                </label>
                <textarea
                  value={formData.career_goal}
                  onChange={(e) =>
                    setFormData({ ...formData, career_goal: e.target.value })
                  }
                  placeholder="E.g., Become an AI researcher, Work in cybersecurity..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent"
                  rows={3}
                />
              </div>

              {/* Weekly Hours */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Available Weekly Hours: {formData.weekly_hours}
                </label>
                <input
                  type="range"
                  min="8"
                  max="20"
                  value={formData.weekly_hours}
                  onChange={(e) =>
                    setFormData({ ...formData, weekly_hours: parseInt(e.target.value) })
                  }
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>8 hrs</span>
                  <span>20 hrs</span>
                </div>
              </div>

              {/* Learning Style */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Learning Style
                </label>
                <select
                  value={formData.learning_style}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      learning_style: e.target.value as any,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent"
                >
                  <option value="hands-on">Hands-on / Practical</option>
                  <option value="theoretical">Theoretical / Research</option>
                  <option value="balanced">Balanced</option>
                </select>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGeneratePlan}
                disabled={loading || formData.interests.length === 0}
                className="w-full bg-[#C41230] text-white py-3 rounded-lg font-semibold hover:bg-[#A01028] transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Generating...' : '✨ Generate Degree Plan'}
              </button>

              {formData.interests.length === 0 && (
                <p className="text-sm text-red-600 mt-2">
                  Please select at least one interest
                </p>
              )}
            </div>
          </div>

          {/* Right Panel: Degree Plan Display */}
          <div className="lg:col-span-2">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-600 font-semibold">{error}</p>
              </div>
            )}

            {loading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#C41230] border-t-transparent"></div>
                <p className="mt-4 text-gray-600">
                  Generating your personalized degree plan...
                </p>
              </div>
            )}

            {!loading && !degreePlan && !error && (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <div className="text-6xl mb-4">🎓</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Ready to Plan Your Degree?
                </h3>
                <p className="text-gray-600 mb-6">
                  Fill out your preferences on the left and click "Generate Degree Plan" to get
                  started.
                </p>
              </div>
            )}

            {!loading && degreePlan && (
              <div>
                <div className="bg-[#C41230] text-white rounded-lg p-6 mb-6">
                  <h2 className="text-2xl font-bold mb-2">Your Personalized Degree Plan</h2>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-red-100">Number of Semesters:</p>
                      <p className="text-xl font-bold">{numberOfSemesters}</p>
                    </div>
                    <div>
                      <p className="text-sm text-red-100">Required Credits:</p>
                      <p className="text-xl font-bold">{userProfile.program ? creditRequirements[userProfile.program] : 144}</p>
                    </div>
                    <div>
                      <p className="text-sm text-red-100">Estimated Weekly Hours:</p>
                      <p className="text-xl font-bold">{degreePlan.estimated_weekly_hours || 12}</p>
                    </div>
                    <div>
                      <p className="text-sm text-red-100">Credits per Semester:</p>
                      <p className="text-xl font-bold">~{Math.ceil((userProfile.program ? creditRequirements[userProfile.program] : 144) / numberOfSemesters)}</p>
                    </div>
                  </div>
                </div>

                {/* Render semesters dynamically based on numberOfSemesters */}
                {numberOfSemesters >= 1 && renderSemesterCourses(
                  'year_1_semester_1',
                  degreePlan.year_1_semester_1 || [],
                  1
                )}
                {numberOfSemesters >= 2 && renderSemesterCourses(
                  'year_1_semester_2',
                  degreePlan.year_1_semester_2 || [],
                  2
                )}
                {numberOfSemesters >= 3 && renderSemesterCourses(
                  'year_2_semester_1',
                  degreePlan.year_2_semester_1 || [],
                  3
                )}
                {numberOfSemesters >= 4 && renderSemesterCourses(
                  'year_2_semester_2',
                  degreePlan.year_2_semester_2 || [],
                  4
                )}

                <div className="flex gap-4 mt-6">
                  <button
                    onClick={() => {
                      const userId = storage.getSessionId();
                      degreePlanAPI.savePlan(userId, degreePlan);
                      alert('Degree plan saved successfully!');
                    }}
                    className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition"
                  >
                    💾 Save Plan
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="flex-1 bg-gray-600 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition"
                  >
                    📄 Export PDF
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DegreePlanPage;
